import { isEqual } from 'lodash';

import {
  BRANCH_TYPE,
  COUNTRY_OPTIONS,
  EINTRAGUNG_ALS_ID,
  EIV_ENTRY_TYPE_TO_GERMAN,
  EIV_ENTRY_TYPE_TO_GERMAN_NAME,
  EIV_STATUS,
  EIV_STATUS_TO_SHOW,
  KEYS_TO_GERMAN,
  TENANT_WITH_BIRTHDATE,
} from 'global/constants';
import { evaluateCondition } from 'global/fieldValidationFunction';

import {
  conditionalChangeEntry,
  conditionalStaticEntry,
  getEintragungAlsIdEntries,
  prepValueForEmail,
  prepValueForInitialData,
} from 'utils/DataUtils';
import { parseEIVDateStringToEfixDateString } from 'utils/DatesUtils';
import { areExpertContactsEnabled } from 'utils/FormUtils';
import { doesRegisteredInstallerHaveBranch } from 'utils/InstallerUtils';
import { QUALIFICATION_ID_FIELDS } from 'utils/qualificationBasisData';
import { getTenantId } from 'utils/tenantUtil';

import { DEBFI_FIRST_PAGE_SECTIONS, getDebFIFieldLabel } from './DebFIFormSectionUtil';
import { prepareDEBInitialData } from './DebFormDataUtil';
import { calculateChangedValues } from './DebFormSectionUtil';

const conditionalExpertEntry = (condition, label, value) =>
  condition
    ? {
        first: label,
        second: {
          first: prepValueForEmail(value),
          second: prepValueForEmail(value),
        },
      }
    : null;
const prepDebFIExpertRow = (expert, oldExpert, actionLabel) => {
  const isNew = !oldExpert;
  const formatValue = (current, previous) => ({
    first: prepValueForEmail(current),
    second: isNew ? prepValueForEmail(current) : prepValueForEmail(previous),
  });

  return [
    conditionalExpertEntry(Boolean(oldExpert?.id), 'Id', oldExpert?.id),
    conditionalExpertEntry(expert.isExpertEnergyActive, 'Sparte Strom', 'Ja'),
    conditionalExpertEntry(expert.isExpertGasActive, 'Sparte Gas', 'Ja'),
    {
      first: 'Vorname',
      second: formatValue(expert.firstName, oldExpert?.firstName),
    },
    {
      first: 'Nachname',
      second: formatValue(expert.lastName, oldExpert?.lastName),
    },
    ...QUALIFICATION_ID_FIELDS.map(([field]) => ({
      first: field,
      second: {
        first: String(expert[field] || 0),
        second: String((isNew ? expert[field] : oldExpert?.[field]) || 0),
      },
    })),
    conditionalChangeEntry(
      areExpertContactsEnabled && expert.telefon,
      'Telefonnummer',
      expert.telefon,
      prepValueForEmail(oldExpert?.telefon)
    ),
    conditionalChangeEntry(
      areExpertContactsEnabled && expert.netzportalEmail,
      'E-Mail-Adresse',
      expert.netzportalEmail,
      prepValueForEmail(oldExpert?.netzportalEmail)
    ),
    conditionalChangeEntry(
      expert.isExpertEnergyActive || expert.isExpertGasActive,
      'HWK Betriebsleiter',
      prepValueForEmail(expert.hwkBetriebsleiter),
      prepValueForEmail(oldExpert?.hwkBetriebsleiter)
    ),
    conditionalChangeEntry(
      expert.isExpertEnergyActive,
      'Stromausweis Gültigkeitsdatum',
      prepValueForEmail(expert.expertEnergyValidityDate),
      prepValueForEmail(oldExpert?.expertEnergyValidityDate)
    ),
    conditionalChangeEntry(
      expert.isExpertGasActive,
      'Gasausweis Gültigkeitsdatum',
      prepValueForEmail(expert.expertGasValidityDate),
      prepValueForEmail(oldExpert?.expertGasValidityDate)
    ),
    conditionalChangeEntry(
      TENANT_WITH_BIRTHDATE.includes(getTenantId()),
      'Geburtsdatum',
      prepValueForEmail(expert.birthdate),
      prepValueForEmail(oldExpert?.birthdate)
    ),
    conditionalChangeEntry(
      Boolean(actionLabel),
      'Änderung',
      actionLabel,
      isNew ? '-' : 'Unverändert'
    ),
  ].filter(Boolean);
};

const prepDebFIExperts = (initialData, firstPageValues) => {
  const expertEntries = [];

  firstPageValues.experts.forEach((expert) => {
    const oldExpert = expert.isNewExpert
      ? undefined
      : initialData.experts.find((search) => String(search.id) === String(expert.id));

    let actionLabel = 'Neu';

    if (oldExpert) {
      actionLabel = isEqual(expert, oldExpert) ? 'Unverändert' : 'Geändert';
    }

    expertEntries.push(prepDebFIExpertRow(expert, oldExpert, actionLabel));
  });

  initialData.experts.forEach((oldExpert) => {
    const stillExists = firstPageValues.experts.some(
      (expert) => !expert.isNewExpert && String(expert.id) === String(oldExpert.id)
    );
    if (!stillExists) {
      expertEntries.push(
        prepDebFIExpertRow(
          {
            ...oldExpert,
            isExpertEnergyActive: oldExpert.isExpertEnergyActive,
            isExpertGasActive: oldExpert.isExpertGasActive,
          },
          oldExpert,
          'Gelöscht'
        )
      );
    }
  });

  return expertEntries;
};

const getInitialTaetigkeitsschwerpunkt = (installer, installerStatusSummary) => {
  const hasEnergyBranch = doesRegisteredInstallerHaveBranch(
    BRANCH_TYPE.ENERGY,
    installerStatusSummary
  );
  const hasGasBranch = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary);

  if (hasEnergyBranch) {
    return installer?.bBetriebsartStrom?.name || '';
  }

  if (hasGasBranch) {
    return installer?.bBetriebsartGas?.name || '';
  }

  return '';
};
export const prepareDEBFICompleteData = (installer, experts, installerStatusSummary) => {
  const baseData = prepareDEBInitialData(installer, experts);

  const mappedExperts = (experts ?? [])
    .filter(
      (expert) =>
        EIV_STATUS_TO_SHOW.includes(expert.statusStrom) ||
        EIV_STATUS_TO_SHOW.includes(expert.statusGas)
    )
    .map((expert) => ({
      firstName: prepValueForInitialData(expert.vorname),
      lastName: prepValueForInitialData(expert.nachname),
      expertEnergyValidityDate: parseEIVDateStringToEfixDateString(
        prepValueForInitialData(expert.gueltigBisStrom)
      ),
      expertGasValidityDate: parseEIVDateStringToEfixDateString(
        prepValueForInitialData(expert.gueltigBisGas)
      ),
      birthdate: parseEIVDateStringToEfixDateString(expert.geburtsdatum),
      telefon: prepValueForInitialData(expert.telefon),
      netzportalEmail: expert.netzportalEmail,
      id: prepValueForInitialData(expert.id),
      bBerufbezStromId: prepValueForInitialData(expert.bBerufbezStromId),
      bBerufbezGasId: prepValueForInitialData(expert.bBerufbezGasId),
      bFachrichtungStromId: prepValueForInitialData(expert.bFachrichtungStromId),
      bFachrichtungGasId: prepValueForInitialData(expert.bFachrichtungGasId),
      bZusatzStromId: prepValueForInitialData(expert.bZusatzStromId),
      bZusatzGasId: prepValueForInitialData(expert.bZusatzGasId),
      hwkBetriebsleiter: prepValueForInitialData(expert.hwkBetriebsleiter),
      isExpertEnergyActive: expert.statusStrom === EIV_STATUS.ACTIVE,
      isExpertGasActive: expert.statusGas === EIV_STATUS.ACTIVE,
      completeName: `${prepValueForInitialData(expert.vorname)} ${prepValueForInitialData(
        expert.nachname
      )}`.trim(),
      energyValidityDate: parseEIVDateStringToEfixDateString(
        prepValueForInitialData(expert.gueltigBisStrom)
      ),
      gasValidityDate: parseEIVDateStringToEfixDateString(
        prepValueForInitialData(expert.gueltigBisGas)
      ),
    }));

  return {
    ...baseData,
    taetigkeitsschwerpunkt: getInitialTaetigkeitsschwerpunkt(installer, installerStatusSummary),
    experts: mappedExperts,
    doesEnergyBranchExist: doesRegisteredInstallerHaveBranch(
      BRANCH_TYPE.ENERGY,
      installerStatusSummary
    ),
    doesGasBranchExist: doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary),
  };
};

const getSelectedBetriebsartId = (value, comboBoxOptions) => {
  const selectedOption = comboBoxOptions?.taetigkeitsschwerpunkt?.find(
    (option) => option.title === value || String(option.value) === String(value)
  );

  return selectedOption?.value || value;
};

export const prepDebFIDataForBackend = (
  initialData,
  firstPageValues,
  secondPageValues,
  subject,
  installerStatusSummary,
  comboBoxOptions
) => {
  const formDataEntries = [];
  const expertEntries = prepDebFIExperts(initialData, firstPageValues);

  DEBFI_FIRST_PAGE_SECTIONS.forEach((section) => {
    if (section.isInstallerContact || !section.fields?.length) {
      return;
    }

    if (
      evaluateCondition(section.whenShouldSectionRender, {
        installerStatusSummary,
        values: firstPageValues,
      })
    ) {
      section.fields.forEach((field) => {
        if (
          field.name !== 'email' &&
          evaluateCondition(field.whenShouldFieldRender, {
            installerStatusSummary,
            values: firstPageValues,
          })
        ) {
          formDataEntries.push({
            first: getDebFIFieldLabel(field.name),
            second: {
              first: prepValueForEmail(
                field.name === 'companyCountry'
                  ? (COUNTRY_OPTIONS.find(
                      (opt) =>
                        opt.value === firstPageValues[field.name] ||
                        opt.title === firstPageValues[field.name]
                    )?.title ?? firstPageValues[field.name])
                  : firstPageValues[field.name],
                field.name,
                comboBoxOptions
              ),
              second: prepValueForEmail(
                field.name === 'companyCountry'
                  ? (COUNTRY_OPTIONS.find(
                      (opt) =>
                        opt.value === initialData[field.name] ||
                        opt.title === initialData[field.name]
                    )?.title ?? initialData[field.name])
                  : initialData[field.name],
                field.name,
                comboBoxOptions
              ),
            },
          });
        }
      });
    }
  });
  const upsertFormDataEntry = (key) => {
    const fieldLabel = KEYS_TO_GERMAN[key];
    if (!fieldLabel) return;

    const hasFormValue = Object.prototype.hasOwnProperty.call(firstPageValues, key);

    const currentValue = hasFormValue
      ? prepValueForInitialData(firstPageValues[key])
      : (initialData[key] ?? null);

    const previousValue = initialData[key] ?? null;

    const nextEntry = {
      first: fieldLabel,
      second: {
        first: prepValueForEmail(currentValue, key, comboBoxOptions),
        second: prepValueForEmail(previousValue, key, comboBoxOptions),
      },
    };

    const existingIndex = formDataEntries.findIndex((entry) => entry.first === fieldLabel);

    if (existingIndex >= 0) {
      formDataEntries[existingIndex] = nextEntry;
    } else {
      formDataEntries.push(nextEntry);
    }
  };

  [
    'companyName1',
    'companyName2',
    'phone',
    'mobile',
    'craftsChamberArea',
    'gridProviderEnergy',
    'gridProviderGas',
    'eintragungsartStrom',
    'eintragungsartGas',
    'foreignCompanyIdEnergy',
    'foreignCompanyIdGas',
    'craftsChamberNumber',
    'craftsChamberDate',
    'businessRegistrationDate',
    'businessRegistrationLocation',
    'commercialRegisterNumber',
    'commercialRegisterLocation',
    'commercialRegisterDate',
  ].forEach(upsertFormDataEntry);

  const hasEnergyRow = doesRegisteredInstallerHaveBranch(
    BRANCH_TYPE.ENERGY,
    installerStatusSummary
  );
  const hasGasRow = doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary);
  const energyEntryType = prepValueForEmail(
    EIV_ENTRY_TYPE_TO_GERMAN[
      firstPageValues.eintragungsartStrom || initialData.eintragungsartStrom
    ] || EIV_ENTRY_TYPE_TO_GERMAN_NAME[installerStatusSummary[BRANCH_TYPE.ENERGY]?.entryType]
  );
  const gasEntryType = prepValueForEmail(
    EIV_ENTRY_TYPE_TO_GERMAN[firstPageValues.eintragungsartGas || initialData.eintragungsartGas] ||
      EIV_ENTRY_TYPE_TO_GERMAN_NAME[installerStatusSummary[BRANCH_TYPE.GAS]?.entryType]
  );

  const baseDataEntries = [
    { first: 'Eintragungsnummer', second: initialData.companyId },
    conditionalStaticEntry(hasEnergyRow, 'Status Strom Eintragung', energyEntryType),
    conditionalStaticEntry(hasGasRow, 'Status Gas Eintragung', gasEntryType),
    conditionalStaticEntry(
      hasEnergyRow,
      'Betriebsart Strom',
      getSelectedBetriebsartId(firstPageValues.taetigkeitsschwerpunkt, comboBoxOptions)
    ),
    conditionalStaticEntry(
      hasGasRow,
      'Betriebsart Gas',
      getSelectedBetriebsartId(firstPageValues.taetigkeitsschwerpunkt, comboBoxOptions)
    ),
    ...getEintragungAlsIdEntries({
      energyId: EINTRAGUNG_ALS_ID.FACHINTERESSENT,
      gasId: EINTRAGUNG_ALS_ID.FACHINTERESSENT,
      hasEnergyBranch: hasEnergyRow,
      hasGasBranch: hasGasRow,
    }),
    { first: 'Anmerkung des Installateurs', second: prepValueForEmail(secondPageValues.changes) },
  ].filter(Boolean);

  return {
    email: initialData.email,
    subject,
    baseDataEntries,
    formDataEntries,
    expertEntries,
    sealsEntries: [],
    documents: [],
    pflegendeBU: installerStatusSummary.pflegendeBu,
    betriebsartStrom: hasEnergyRow
      ? getSelectedBetriebsartId(firstPageValues.taetigkeitsschwerpunkt, comboBoxOptions)
      : installerStatusSummary.betriebsartStrom,
    betriebsartGas: hasGasRow
      ? getSelectedBetriebsartId(firstPageValues.taetigkeitsschwerpunkt, comboBoxOptions)
      : installerStatusSummary.betriebsartGas,
  };
};

export const createEmailSubjectForDebFI = (
  firstPageValues,
  initialData,
  installerStatusSummary
) => {
  const changedValues = calculateChangedValues(firstPageValues, initialData);

  let subject = '[e-fix] ';
  const branches = [];

  if (doesRegisteredInstallerHaveBranch(BRANCH_TYPE.ENERGY, installerStatusSummary)) {
    branches.push('Strom');
  }
  if (doesRegisteredInstallerHaveBranch(BRANCH_TYPE.GAS, installerStatusSummary)) {
    branches.push('Gas');
  }

  subject += `${branches.join(' ')} - Änderung`;

  if (Object.keys(changedValues).length === 0) {
    return subject;
  }

  const energyType =
    EIV_ENTRY_TYPE_TO_GERMAN_NAME[installerStatusSummary[BRANCH_TYPE.ENERGY]?.entryType];
  const gasType = EIV_ENTRY_TYPE_TO_GERMAN_NAME[installerStatusSummary[BRANCH_TYPE.GAS]?.entryType];

  if (energyType) {
    subject += ` - ${energyType}`;
  } else if (gasType) {
    subject += ` - ${gasType}`;
  }

  return subject;
};
